<template>
  <div>
    <v-tooltip bottom v-if="!isReadOnlyUser">
      <template v-slot:activator="{ on }">
        <v-icon medium class="mr-2" color="primary" v-on="on" @click.stop="openLogDialog">
          mdi-clipboard-check
        </v-icon>
      </template>
      <span>{{ $t("proof.logProof") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="!isReadOnlyUser">
      <template v-slot:activator="{ on }">
        <v-icon medium class="mr-2" v-on="on" @click.stop="openMoveDialog">
          content_paste_go
        </v-icon>
      </template>
      <span>{{ $t("proof.move") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="canCreateHere">
      <template v-slot:activator="{ on }">
        <v-icon medium class="mr-2" v-on="on" @click.stop="copyItem">content_copy</v-icon>
      </template>
      <span>{{ $t("proof.copy") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="canDeleteHere">
      <template v-slot:activator="{ on }">
        <v-icon medium class="mr-2" v-on="on" @click.stop="openDeleteDialog">delete</v-icon>
      </template>
      <span>{{ $t("proof.delete") }}</span>
    </v-tooltip>

    <v-dialog v-model="logDialog" max-width="520" persistent>
      <v-card>
        <v-card-title>{{ $t("proof.logProof") }}</v-card-title>
        <v-card-text>
          <div class="mb-4">{{ $t("proof.logProofConfirm", { name: item.name }) }}</div>
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formattedLogDate"
                :label="$t('proof.logProofDate')"
                prepend-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="logDate"
              locale="de"
              no-title
              @input="dateMenu = false"
            />
          </v-menu>
          <v-file-input
            v-model="logFiles"
            :label="$t('proof.form.historyFiles')"
            prepend-icon="mdi-paperclip"
            outlined
            dense
            multiple
            small-chips
            hide-details
            class="mt-2"
          />
          <template v-if="logChecklist.length">
            <div class="subtitle-2 mt-5 mb-1">
              {{ $t("proof.form.checklistSection") }}
            </div>
            <v-checkbox
              v-for="(task, idx) in logChecklist"
              :key="idx"
              v-model="task.done"
              :label="task.task"
              dense
              hide-details
              class="mt-1"
            />
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="logDialog = false">{{ $t("general.cancel") }}</v-btn>
          <v-btn color="primary" :loading="logging" @click="confirmLog">
            {{ $t("proof.logProofAction") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <deleteDialog @triggerDelete="deleteItem" :item="item" :deleteDialog="deleteDialog" />
    <moveDialog
      :item="item"
      :moveDialog="moveDialog"
      :loading="moving"
      :array="proofCategories"
      @cancel="moveDialog.open = false"
      @reload="reloadDataFunc"
      @confirm="handleMoveConfirm"
    />
  </div>
</template>

<script>
import deleteDialog from "../../_utils/dataTableHelpers/actions/deleteDialog.vue";
import moveDialog from "../../_utils/dataTableHelpers/actions/moveDialog.vue";

export default {
  props: ["item"],
  components: { deleteDialog, moveDialog },
  data() {
    return {
      logDialog: false,
      logging: false,
      logDate: new Date().toISOString().substr(0, 10),
      logFiles: [],
      logChecklist: [],
      dateMenu: false,
      deleteDialog: { open: false, title: "proof.delete", description: "" },
      moveDialog: { open: false, title: "proof.move", description: "", targetId: null },
      moving: false,
      proofCategories: [],
    };
  },
  computed: {
    formattedLogDate() {
      if (!this.logDate) return "";
      const [y, m, d] = this.logDate.split("-");
      return `${d}.${m}.${y}`;
    },
  },
  methods: {
    openLogDialog() {
      this.logDate = new Date().toISOString().substr(0, 10);
      this.logFiles = [];
      this.logChecklist = (this.item.checklist || []).map((c) => ({
        task: c.task,
        done: false,
      }));
      this.logDialog = true;
    },
    readFileContent(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(",")[1]);
        reader.readAsDataURL(file);
      });
    },
    async confirmLog() {
      this.logging = true;
      try {
        const id = this.item._id || this.item.id;
        const updated = await this.$store.dispatch("logProof", {
          id,
          date: this.logDate,
          completedTasks: this.logChecklist
            .filter((t) => t.done)
            .map((t) => t.task),
        });
        if (updated && this.logFiles.length) {
          const historyIndex = (updated.proofHistory || []).length - 1;
          for (const file of this.logFiles) {
            const content = await this.readFileContent(file);
            if (!content) continue;
            await this.$store.dispatch("uploadProofDocument", {
              id,
              content,
              name: file.name,
              filetype: file.name.split(".").pop().toLowerCase(),
              historyIndex,
            });
          }
        }
        this.logDialog = false;
        this.reloadDataFunc();
      } finally {
        this.logging = false;
      }
    },
    copyItem() {
      this.$router.push({ name: "proofForm", params: { id: this.item._id || this.item.id, mode: "copy" } });
    },
    openDeleteDialog() {
      this.deleteDialog.open = true;
      this.deleteDialog.description = this.$t("proof.deleteLong", { name: this.item.name });
    },
    deleteItem(item) {
      this.$store.dispatch("deleteProof", item).then(() => {
        this.reloadDataFunc();
        this.deleteDialog.open = false;
      });
    },
    reloadDataFunc() {
      this.$emit("reloadData");
    },
    async openMoveDialog() {
      this.moveDialog.open = true;
      this.moveDialog.description = this.$t("proof.movingProof", { name: this.item.name });
      const res = await this.$store.dispatch("getAllProofCategories");
      const all = Array.isArray(res) ? res : res?.data || [];
      const currentId = this.$route.params.proofCategoryId;
      this.proofCategories = all.filter((c) => String(c.id || c._id) !== String(currentId));
    },
    async handleMoveConfirm(targetId) {
      try {
        this.moving = true;
        await this.$store.dispatch("moveProof", {
          id: this.item._id || this.item.id,
          category: targetId,
        });
        this.moveDialog.open = false;
        this.reloadDataFunc();
      } finally {
        this.moving = false;
      }
    },
  },
};
</script>
